import React, { useState, useEffect } from "react";
import {
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Search, AdjustmentsHorizontal } from "tabler-icons-react";
import { useTranslation } from "react-i18next";
import CloseIcon from "../assets/CloseIcon";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation } from "react-router-dom";

// remove selectable comments

const useStyles = makeStyles((theme) => ({
  formControl: {
    backgroundColor: "#44444F",
    borderRadius: "40px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  select: {
    height: "40px",
    borderRadius: "40px",
    backgroundColor: "#44444F",
    color: "#ffffff",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "21px",
    letterSpacing: "0em",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "row-reverse",
  },
  menuPaper: {
    backgroundColor: "#44444F",
    borderRadius: "20px",
    color: "#ffffff",
  },
  customIcon: {
    color: "#ffffff",
    marginLeft: "10px",
  },
}));

const OrgaList = ({
  profilesList,
  selectedOrganization,
  handleOrganizationProfile,
  handleCloseList,
  openList,
  isMobile,
  searchText,
  handleSearchTextChange,
  handleOrgaStatus,
  totalPages,
  handlePage,
  page,
}) => {
  // console.log("selectedOrganization:", selectedOrganization);
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const [filter, setFilter] = useState("all");
  const [profiles, setProfiles] = useState([]);
  const isSelected = (profile) =>
    String(selectedOrganization?.id ?? "") === String(profile?.id ?? "");

  const normalizeWorkflowStatus = (rawValue) => {
    const value = String(rawValue ?? "").trim().toLowerCase();
    if (value === "in_progress") {
      return "in_progress";
    }
    if (value === "not_started") {
      return "not_started";
    }
    if (value === "failure") {
      return "failed";
    }
    return value;
  };
  const normalizeActiveReport = (rawValue) =>
    String(rawValue ?? "").trim().toLowerCase();
  const isCompleted = (status) => status === "completed";

  const path = location.pathname || "";

  const requiresProfile =
    path === "/profile" || path.startsWith("/profile/");

  const requiresEvaluation =
    path === "/evaluation" ||
    path.startsWith("/evaluation/");

  const requiresBusinessFunction =
    path === "/orga_assets" ||
    path.startsWith("/orga_assets/");

  const requiresAssetsDiscovery =
    path === "/asset_inventory" || path.startsWith("/asset_inventory/");

  const requiresRiskScenarios =
    path === "/risk-scenarios" || path.startsWith("/risk-scenarios/");

  const requiresRiskAssessment =
    path === "/risk-assessment" || path.startsWith("/risk-assessment/");

  const requiresReports =
    path === "/reports" || path.startsWith("/reports/");

  const isOrgaSelectable = (profile) => {
    const activeReport = normalizeActiveReport(profile?.active_report);
    const isFlashReportActive = activeReport === "flash_report";
    const isRiskReportActive = activeReport === "risk_report";

    const reconnaissanceStatus = normalizeWorkflowStatus(profile?.reconnaissance_status);
    const assetsDiscoveryStatus = normalizeWorkflowStatus(profile?.assets_discovery_status);
    const riskScenariosStatus = normalizeWorkflowStatus(profile?.risk_scenarios_status);
    const riskAssessmentStatus = normalizeWorkflowStatus(profile?.risk_assesment_status);
    const generateReportStatus = normalizeWorkflowStatus(profile?.generate_report_status);
    const hasCompletedReports = Boolean(profile?.has_completed_reports);

    const canAccessEvaluation = !isFlashReportActive && isRiskReportActive;
    const canAccessReconnaissance =
      !isFlashReportActive && isCompleted(reconnaissanceStatus);
    const canAccessAssetsDiscovery =
      !isFlashReportActive && isCompleted(assetsDiscoveryStatus);
    const canAccessRiskScenarios =
      !isFlashReportActive && isCompleted(riskScenariosStatus);
    const canAccessRiskAssessment =
      !isFlashReportActive && isCompleted(riskAssessmentStatus);
    const canAccessReports =
      isCompleted(generateReportStatus) || hasCompletedReports;

    if (requiresEvaluation) return canAccessEvaluation;
    if (requiresProfile) return canAccessReconnaissance;
    if (requiresBusinessFunction) return canAccessReconnaissance;
    if (requiresAssetsDiscovery) return canAccessAssetsDiscovery;
    if (requiresRiskScenarios) return canAccessRiskScenarios;
    if (requiresRiskAssessment) return canAccessRiskAssessment;
    if (requiresReports) return canAccessReports;
    return true;
  };

  useEffect(() => {
    let uniqueIds = {};
    let filtredList = profilesList.filter((obj) => {
      if (!uniqueIds[obj.id]) {
        uniqueIds[obj.id] = true;
        return true;
      }
      return false;
    });

    setProfiles(filtredList);
  }, [profilesList]);

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop === clientHeight && totalPages > page) {
      handlePage(page + 1);
    }
  };

  useEffect(() => {
    if (filter === "pending") {
      handleOrgaStatus("pending");
    } else if (filter === "completed") {
      handleOrgaStatus("completed");
    } else {
      handleOrgaStatus("");
    }
  }, [filter]);

  return (
    <div
      className={
        isMobile
          ? openList
            ? "orga_list-open-mobile"
            : "orga_list-mobile"
          : openList
          ? "orga_list-open"
          : "orga_list"
      }
    >
      {openList && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="title_card">{t("orga_list")}</p>
            <div onClick={() => handleCloseList()}>
              <CloseIcon />
            </div>
          </div>

          <Grid container spacing={2} mt={0}>
            <Grid item sm={12} xs={8} md={9}>
              <TextField
                fullWidth
                id="outlined-start-adornment"
                placeholder={t("search")}
                variant="standard"
                value={searchText}
                onChange={(e) => handleSearchTextChange(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      {" "}
                      <Search size={25} strokeWidth={2} color="#ffffff" />
                    </InputAdornment>
                  ),
                  style: {
                    backgroundColor: "#44444F",
                    borderRadius: "40px",
                    height: "40px",
                    paddingLeft: "10px",
                  },
                  inputProps: {
                    style: {
                      color: "#ffffff",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: "600",
                      lineHeight: "21px",
                      letterSpacing: "0em",
                      textAlign: "left",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item sm={6} xs={4} md={3}>
              <FormControl
                fullWidth
                className={classes.formControl}
                variant="outlined"
              >
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  className={classes.select}
                  label={t("filter_by")}
                  value={filter}
                  variant="outlined"
                  IconComponent={() => (
                    <AdjustmentsHorizontal className={classes.customIcon} />
                  )}
                  // style={{ height: "auto" }}
                  onChange={(e) => setFilter(e.target.value)}
                  MenuProps={{
                    classes: {
                      paper: classes.menuPaper,
                    },
                  }}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem value={"completed"}>Completed</MenuItem>
                  <MenuItem value={"pending"}>Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <div className="organisations" onScroll={handleScroll}>
            {profiles.map((profile, index) => {
              const selectable = isOrgaSelectable(profile);
              return (
                <div
                  key={profile.id ?? index}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Tooltip
                    title={
                      !selectable
                        ? t("step_not_completed") || "Step not completed"
                        : ""
                    }
                    arrow
                    placement="right"
                  >
                    <div
                      id="item"
                      style={{
                        color:
                          !isSelected(profile) && "#73767B",
                        padding: "0 15px",
                        position: "relative",
                        cursor: selectable ? "pointer" : "not-allowed",
                        opacity: selectable ? 1 : 0.5,
                      }}
                      onClick={() =>
                        selectable && handleOrganizationProfile(profile)
                      }
                    >
                      {isSelected(profile) && (
                        <div
                          style={{
                            justifyItems: "start",
                            marginLeft: "-10px",
                            position: "absolute",
                            left: "10px",
                          }}
                        >
                          <svg
                            width="7"
                            height="50"
                            viewBox="0 0 3 61"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 0.5C1.65685 0.5 3 1.84315 3 3.5V57.5C3 59.1569 1.65685 60.5 0 60.5V0.5Z"
                              fill="#78C3AE"
                            />
                          </svg>
                        </div>
                      )}
                      <svg
                        width="30"
                        height="30"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.30493 18.0532C4.66169 8.2776 10.9585 5.59886 10.9585 5.59886C7.51437 6.01633 6.95775 7.82534 6.95775 7.82534L5.11395 7.79055C8.87113 3.33759 14.6113 4.10295 14.6113 4.10295C22.7866 5.45971 25.7785 16.5225 20.4906 21.0451C25.1523 18.262 25.1523 8.66027 20.5949 6.25985L23.9694 6.78168L20.3862 3.30281L16.5247 3.37238L16.4203 2.18957C12.9066 0.798015 11.0628 0.276184 11.0628 0.276184L9.49733 3.72027L7.89705 1.31985C1.00887 3.68548 0 11.0259 0 11.0259C1.04366 8.52112 4.24423 8.48633 4.24423 8.48633C1.04366 9.70394 0.104366 17.3923 0.104366 17.3923L3.65282 13.0784L2.43521 17.7749C2.40042 19.7927 4.31381 19.8623 4.31381 19.8623C3.27014 17.4966 4.97479 18.088 4.97479 18.088L5.39226 17.0444L4.69648 17.6706L4.03549 17.2879L3.30493 18.0532Z"
                          fill={
                            isSelected(profile)
                              ? "white"
                              : "#73767B"
                          }
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M7.34043 10.2606L7.06212 10.9911L6.64465 11.1303L6.71423 10.678L7.34043 10.2606ZM9.18423 8.76464C4.45296 10.7476 6.33155 12.8349 6.33155 12.8349L6.5055 11.7217L7.65352 11.2346C7.34043 9.98225 9.18423 8.76464 9.18423 8.76464Z"
                          fill={
                            isSelected(profile)
                              ? "white"
                              : "#73767B"
                          }
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12.6979 8.34717C15.2723 16.0703 7.86226 14.5396 7.86226 14.5396C5.53141 16.6269 6.08803 17.5314 6.08803 17.5314C7.51437 16.0355 9.77564 15.8963 9.77564 15.8963C7.23606 17.601 7.34043 19.3056 7.34043 19.3056L5.21831 20.2101C8.21014 23.689 13.2197 23.7238 13.2197 23.7238C9.91479 22.8541 8.0362 21.1146 8.0362 21.1146C13.3589 18.9577 13.9851 15.0962 13.9851 15.0962C16.1768 16.1399 16.0028 19.4448 16.0028 19.4448C18.438 10.2953 12.6979 8.34717 12.6979 8.34717Z"
                          fill={
                            isSelected(profile)
                              ? "white"
                              : "#73767B"
                          }
                        />
                      </svg>
                      {profile.name_A}
                    </div>
                  </Tooltip>
                  {/* {profile?.tabs_completed !== 7 && <PriorityHighRoundedIcon />} */}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default OrgaList;
